/**
 * EntityCalendarTab — the Calendar tab shared by Student detail and Staff
 * detail (11E.7).
 *
 * This is the frontend half of the cross-reflection invariant: a session's
 * roster (`student_ids`, maintained by bookings) and its
 * `attendance_marked_at` stamp must read identically here as they do on
 * the main calendar. Rather than build a second query shape for "sessions
 * for one entity", this reuses `useSessions` with `student_id`/`teacher_id`
 * — the exact filter the backend `list_sessions` service already supports
 * for the main calendar's teacher-scoping — so there is no second source
 * of truth to drift from the calendar grid.
 *
 * Deliberately NOT a mini calendar grid: a flat, chronological list reads
 * better in a detail-page tab's narrower column, and every row still links
 * to the full session page for anything beyond "what's scheduled".
 */

import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/common';
import { useAuth } from '@/context';
import { useSubjectOptions, useTeacherOptions } from '@features/calendar/api/lookups';
import { useSessions, type SessionResponse } from '@features/calendar/api/sessions';
import { fmtIsoDate, fmtTimeInTz, inTenantTz, tenantWallClockToUtc } from '@features/calendar/utils/tz';

export interface EntityCalendarTabProps {
  /** Exactly one of these identifies whose sessions to show. */
  studentId?: string;
  teacherId?: string;
  /** Opens the full session page. Injected so this stays router-agnostic. */
  onOpenSession?: (session: SessionResponse) => void;
  /** Weeks to look back / ahead from today. */
  weeksBack?: number;
  weeksAhead?: number;
}

/**
 * Today's date in the tenant timezone, re-evaluated as the tenant day rolls
 * over.
 *
 * Deriving this from `new Date()` inside a render is wrong twice over: it
 * produces a fresh value every render (defeating every memo downstream), and
 * memoising it on `tenantTz` alone freezes it — a detail page left open
 * across midnight would keep filtering "past sessions" against yesterday.
 * Polling once a minute rather than scheduling a single timeout to midnight
 * keeps this correct in a backgrounded tab, where long timers are throttled
 * and can fire arbitrarily late. Returning `prev` unchanged means the common
 * case costs nothing.
 */
function useTenantToday(tenantTz: string): string {
  const [today, setToday] = useState(() => fmtIsoDate(inTenantTz(new Date(), tenantTz)));
  useEffect(() => {
    const sync = () =>
      setToday((prev) => {
        const next = fmtIsoDate(inTenantTz(new Date(), tenantTz));
        return next === prev ? prev : next;
      });
    sync();
    const id = window.setInterval(sync, 60_000);
    return () => window.clearInterval(id);
  }, [tenantTz]);
  return today;
}

export const EntityCalendarTab: React.FC<EntityCalendarTabProps> = ({
  studentId,
  teacherId,
  onOpenSession,
  weeksBack = 2,
  weeksAhead = 4,
}) => {
  const { user } = useAuth();
  const tenantTz = user?.tenant_timezone ?? 'UTC';
  const [showPast, setShowPast] = useState(false);

  const { data: subjectOptions } = useSubjectOptions();
  const subjectNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of subjectOptions ?? []) m[o.value] = o.label;
    return m;
  }, [subjectOptions]);
  const { data: teacherOptions } = useTeacherOptions();
  const teacherNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of teacherOptions ?? []) m[o.value] = o.label;
    return m;
  }, [teacherOptions]);

  const todayIso = useTenantToday(tenantTz);

  const { fromIso, toIso } = useMemo(() => {
    // Anchored on `todayIso` rather than a fresh `new Date()` so the window
    // rolls forward with the tenant day instead of pinning to whenever this
    // tab first mounted. Only the y/m/d fields are read below, and parsing a
    // bare `YYYY-MM-DDT00:00:00` gives exactly those in local time.
    const today = new Date(`${todayIso}T00:00:00`);
    const start = new Date(today);
    start.setDate(start.getDate() - weeksBack * 7);
    const end = new Date(today);
    end.setDate(end.getDate() + weeksAhead * 7);
    return {
      fromIso: tenantWallClockToUtc(
        start.getFullYear(), start.getMonth(), start.getDate(), 0, tenantTz,
      ).toISOString(),
      toIso: tenantWallClockToUtc(
        end.getFullYear(), end.getMonth(), end.getDate(), 0, tenantTz,
      ).toISOString(),
    };
  }, [tenantTz, weeksBack, weeksAhead, todayIso]);

  const { data: sessions, isLoading, isError, refetch } = useSessions({
    from: fromIso,
    to: toIso,
    student_id: studentId,
    teacher_id: teacherId,
  });

  const visible = useMemo(() => {
    const list = (sessions ?? []).slice().sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    if (showPast) return list;
    return list.filter((s) => fmtIsoDate(inTenantTz(new Date(s.starts_at), tenantTz)) >= todayIso);
  }, [sessions, showPast, tenantTz, todayIso]);

  if (isLoading) {
    return <div data-testid="candeza-entity-calendar-loading">Loading calendar…</div>;
  }
  if (isError) {
    return (
      <div data-testid="candeza-entity-calendar-error" style={{ color: '#dc2626' }}>
        Failed to load sessions.{' '}
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="candeza-entity-calendar-tab">
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <input
          type="checkbox"
          data-testid="candeza-entity-calendar-show-past"
          checked={showPast}
          onChange={(e) => setShowPast(e.target.checked)}
        />
        Show past sessions
      </label>

      {visible.length === 0 ? (
        <div data-testid="candeza-entity-calendar-empty" style={{ color: '#64748b', fontSize: 14 }}>
          No sessions scheduled.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12 }}>
              <th style={{ padding: '6px 4px' }}>Date</th>
              <th style={{ padding: '6px 4px' }}>Time</th>
              <th style={{ padding: '6px 4px' }}>Subject</th>
              <th style={{ padding: '6px 4px' }}>{studentId ? 'Staff' : 'Booked'}</th>
              <th style={{ padding: '6px 4px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr
                key={s.id}
                data-testid={`candeza-entity-calendar-row-${s.id}`}
                data-session-status={s.status}
                data-attendance-marked={s.attendance_marked_at ? 'true' : 'false'}
                onClick={() => onOpenSession?.(s)}
                style={{
                  borderTop: '1px solid #e2e8f0',
                  cursor: onOpenSession ? 'pointer' : 'default',
                }}
              >
                <td style={{ padding: '6px 4px' }}>
                  {inTenantTz(new Date(s.starts_at), tenantTz).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short',
                  })}
                </td>
                <td style={{ padding: '6px 4px' }}>{fmtTimeInTz(s.starts_at, tenantTz)}</td>
                <td style={{ padding: '6px 4px' }}>
                  {subjectNames[s.subject_id] ?? s.subject_id}
                </td>
                <td style={{ padding: '6px 4px' }}>
                  {studentId
                    ? (teacherNames[s.teacher_id] ?? s.teacher_id)
                    : `${s.booked_count ?? s.student_ids?.length ?? 0}${
                        s.capacity != null ? `/${s.capacity}` : ''
                      }`}
                </td>
                <td style={{ padding: '6px 4px' }}>
                  {s.status === 'cancelled' ? (
                    <span style={{ color: '#dc2626' }}>Cancelled</span>
                  ) : s.attendance_marked_at ? (
                    <span
                      data-testid={`candeza-entity-calendar-attendance-${s.id}`}
                      style={{ color: '#166534' }}
                    >
                      ✓ Marked
                    </span>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>Scheduled</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
